import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getAuditEvents, formatTimestamp, getDefaultTimeWindow } from '../../services/auditService';

export default function ActivityPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Initialize filters from URL params
  const requestIdFromUrl = searchParams.get('request_id') || '';

  // Filters
  const [filters, setFilters] = useState({
    actor_id: '',
    action: '',
    outcome: '',
    q: requestIdFromUrl,
    request_id: requestIdFromUrl,
    since: '',
    until: '',
  });

  // Load initial data
  useEffect(() => {
    loadEvents();
  }, []);

  // Update filters when URL params change
  useEffect(() => {
    const requestId = searchParams.get('request_id');
    if (requestId && requestId !== filters.request_id) {
      setFilters((prev) => ({
        ...prev,
        request_id: requestId,
        q: requestId, // Also set search to request_id for filtering
      }));
      // Reload events with new filter
      setTimeout(() => {
        loadEvents();
      }, 0);
    }
  }, [searchParams]);

  const loadEvents = async (cursor = null) => {
    try {
      if (cursor) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Build filters
      const queryFilters = {
        limit: 50,
        ...(cursor && { cursor }),
      };

      // Add time window if not provided (default to last 24 hours)
      if (!filters.since && !filters.until) {
        const defaultWindow = getDefaultTimeWindow();
        queryFilters.since = defaultWindow.since;
        queryFilters.until = defaultWindow.until;
      } else {
        if (filters.since) queryFilters.since = filters.since;
        if (filters.until) queryFilters.until = filters.until;
      }

      // Add other filters
      if (filters.actor_id) queryFilters.actor_id = filters.actor_id;
      if (filters.action) queryFilters.action = filters.action;
      if (filters.outcome) queryFilters.outcome = filters.outcome;
      if (filters.request_id) {
        queryFilters.request_id = filters.request_id;
      } else if (filters.q) {
        queryFilters.q = filters.q;
      }

      const response = await getAuditEvents(queryFilters);

      if (cursor) {
        // Append to existing events
        setEvents((prev) => [...prev, ...(response.items || [])]);
      } else {
        // Replace events
        setEvents(response.items || []);
      }

      setNextCursor(response.nextCursor || null);
    } catch (err) {
      setError(err.message || 'Failed to load audit events');
      console.error('Error loading audit events:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    setNextCursor(null);
    loadEvents();
  };

  const handleClearFilters = () => {
    setFilters({
      actor_id: '',
      action: '',
      outcome: '',
      q: '',
      since: '',
      until: '',
    });
    setNextCursor(null);
    // Reload with default time window
    setTimeout(() => {
      loadEvents();
    }, 0);
  };

  const handleLoadMore = () => {
    if (nextCursor && !loadingMore) {
      loadEvents(nextCursor);
    }
  };

  const handleRequestIdClick = (requestId) => {
    // Update URL and filters
    setSearchParams({ request_id: requestId });
    setFilters((prev) => ({
      ...prev,
      request_id: requestId,
      q: requestId,
    }));
    setNextCursor(null);
    // Reload with request_id filter
    setTimeout(() => {
      loadEvents();
    }, 0);
  };

  if (loading && events.length === 0) {
    return <div style={styles.loading}>Loading audit events...</div>;
  }

  if (error && events.length === 0) {
    return (
      <div style={styles.error} role="alert" aria-live="polite">
        <p>Error: {error}</p>
        <button onClick={() => loadEvents()} style={styles.button} type="button" aria-label="Retry loading events">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.header}>Activity</h1>

      {/* Filters */}
      <div style={styles.filters}>
        <div style={styles.filterRow}>
          <div style={styles.filterGroup}>
            <label htmlFor="filter-actor-id" style={styles.label}>
              Actor ID:
              <input
                id="filter-actor-id"
                type="text"
                value={filters.actor_id}
                onChange={(e) => handleFilterChange('actor_id', e.target.value)}
                placeholder="Filter by actor ID"
                style={styles.input}
                aria-label="Filter by actor ID"
              />
            </label>
          </div>
          <div style={styles.filterGroup}>
            <label htmlFor="filter-action" style={styles.label}>
              Action:
              <input
                id="filter-action"
                type="text"
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
                placeholder="e.g., integration.enable"
                style={styles.input}
                aria-label="Filter by action"
              />
            </label>
          </div>
          <div style={styles.filterGroup}>
            <label htmlFor="filter-outcome" style={styles.label}>
              Outcome:
              <select
                id="filter-outcome"
                value={filters.outcome}
                onChange={(e) => handleFilterChange('outcome', e.target.value)}
                style={styles.select}
                aria-label="Filter by outcome"
              >
                <option value="">All</option>
                <option value="success">Success</option>
                <option value="failure">Failure</option>
              </select>
            </label>
          </div>
        </div>
        <div style={styles.filterRow}>
          <div style={styles.filterGroup}>
            <label htmlFor="filter-search" style={styles.label}>
              Search:
              <input
                id="filter-search"
                type="text"
                value={filters.q}
                onChange={(e) => handleFilterChange('q', e.target.value)}
                placeholder="Search target ID or metadata"
                style={styles.input}
                aria-label="Search target ID or metadata"
              />
            </label>
          </div>
          <div style={styles.filterGroup}>
            <label htmlFor="filter-since" style={styles.label}>
              Since:
              <input
                id="filter-since"
                type="datetime-local"
                value={filters.since}
                onChange={(e) => handleFilterChange('since', e.target.value ? new Date(e.target.value).toISOString() : '')}
                style={styles.input}
                aria-label="Filter events since this date and time"
              />
            </label>
          </div>
          <div style={styles.filterGroup}>
            <label htmlFor="filter-until" style={styles.label}>
              Until:
              <input
                id="filter-until"
                type="datetime-local"
                value={filters.until ? new Date(filters.until).toISOString().slice(0, 16) : ''}
                onChange={(e) => handleFilterChange('until', e.target.value ? new Date(e.target.value).toISOString() : '')}
                style={styles.input}
                aria-label="Filter events until this date and time"
              />
            </label>
          </div>
        </div>
        <div style={styles.filterActions}>
          <button onClick={handleApplyFilters} style={styles.button} type="button" aria-label="Apply filters">
            Apply Filters
          </button>
          <button onClick={handleClearFilters} style={{ ...styles.button, ...styles.buttonSecondary }} type="button" aria-label="Clear all filters">
            Clear Filters
          </button>
        </div>
      </div>

      {/* Events Table */}
      {events.length === 0 ? (
        <div style={styles.empty}>
          <p>No activity found for the selected filters.</p>
        </div>
      ) : (
        <>
          <div style={styles.tableContainer}>
            <table style={styles.table} role="table" aria-label="Audit events">
              <thead>
                <tr>
                  <th style={styles.th} scope="col">Timestamp</th>
                  <th style={styles.th} scope="col">Actor</th>
                  <th style={styles.th} scope="col">Action</th>
                  <th style={styles.th} scope="col">Target</th>
                  <th style={styles.th} scope="col">Outcome</th>
                  <th style={styles.th} scope="col">Request ID</th>
                  <th style={styles.th} scope="col">Reason</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id} style={styles.tr}>
                    <td style={styles.td}>{formatTimestamp(event.ts)}</td>
                    <td style={styles.td}>
                      {event.actor_id ? (
                        <button
                          onClick={() => navigate(`/admin/users/${event.actor_id}`)}
                          style={styles.linkButton}
                          type="button"
                          aria-label={`View user ${event.actor_id}`}
                        >
                          <code style={styles.code}>{event.actor_id}</code>
                        </button>
                      ) : (
                        <code style={styles.code}>-</code>
                      )}
                    </td>
                    <td style={styles.td}>
                      <code style={styles.code}>{event.action}</code>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.targetType}>{event.target_type}</span>
                      {event.target_id && (
                        <>
                          {' / '}
                          {event.target_type === 'user' ? (
                            <button
                              onClick={() => navigate(`/admin/users/${event.target_id}`)}
                              style={styles.linkButton}
                              type="button"
                              aria-label={`View user ${event.target_id}`}
                            >
                              <code style={styles.code}>{event.target_id}</code>
                            </button>
                          ) : event.target_type === 'integration' ? (
                            <button
                              onClick={() => navigate(`/admin/api-controller?integration=${encodeURIComponent(event.target_id)}`)}
                              style={styles.linkButton}
                              type="button"
                              aria-label={`View integration ${event.target_id}`}
                            >
                              <code style={styles.code}>{event.target_id}</code>
                            </button>
                          ) : (
                            <code style={styles.code}>{event.target_id}</code>
                          )}
                        </>
                      )}
                    </td>
                    <td style={styles.td}>
                      <OutcomeBadge outcome={event.result} />
                    </td>
                    <td style={styles.td}>
                      {event.request_id ? (
                        <button
                          onClick={() => handleRequestIdClick(event.request_id)}
                          style={styles.linkButton}
                          aria-label={`Filter by request ID ${event.request_id}`}
                          type="button"
                        >
                          <code style={styles.code}>{event.request_id}</code>
                        </button>
                      ) : (
                        <span style={styles.muted}>-</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {event.reason ? (
                        <span style={styles.reason}>{event.reason}</span>
                      ) : (
                        <span style={styles.muted}>-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Load More */}
          {nextCursor && (
            <div style={styles.loadMore}>
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                style={styles.button}
              >
                {loadingMore ? 'Loading...' : 'Load More'}
                <span className="sr-only">Load more audit events</span>
              </button>
            </div>
          )}

          {/* Error state (if error occurs after initial load) */}
          {error && events.length > 0 && (
            <div style={styles.errorBanner} role="alert" aria-live="polite">
              <p>Error: {error}</p>
              <button onClick={() => loadEvents()} style={styles.button} type="button" aria-label="Retry loading events">
                Retry
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function OutcomeBadge({ outcome }) {
  const color = outcome === 'success' ? '#10b981' : '#ef4444';
  return (
    <span
      style={{
        ...styles.badge,
        backgroundColor: color,
        color: '#fff',
      }}
    >
      {outcome || 'unknown'}
    </span>
  );
}

const styles = {
  container: {
    maxWidth: '1400px',
  },
  header: {
    fontSize: '2rem',
    marginBottom: '1.5rem',
    color: '#111827',
  },
  loading: {
    padding: '2rem',
    textAlign: 'center',
    color: '#6b7280',
  },
  error: {
    padding: '2rem',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    borderRadius: '0.5rem',
  },
  errorBanner: {
    padding: '1rem',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  empty: {
    padding: '2rem',
    textAlign: 'center',
    color: '#6b7280',
  },
  filters: {
    backgroundColor: '#fff',
    padding: '1.5rem',
    borderRadius: '0.5rem',
    marginBottom: '1.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  filterRow: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1rem',
    flexWrap: 'wrap',
  },
  filterGroup: {
    flex: '1',
    minWidth: '200px',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '500',
    color: '#374151',
    fontSize: '0.875rem',
  },
  input: {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    outline: 'none',
  },
  inputFocus: {
    outline: '2px solid #3b82f6',
    outlineOffset: '2px',
    borderColor: '#3b82f6',
  },
  select: {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    outline: 'none',
  },
  selectFocus: {
    outline: '2px solid #3b82f6',
    outlineOffset: '2px',
    borderColor: '#3b82f6',
  },
  filterActions: {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '1rem',
  },
  button: {
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: '0.375rem',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    backgroundColor: '#3b82f6',
    color: '#fff',
    transition: 'background-color 0.2s',
    outline: 'none',
  },
  buttonFocus: {
    outline: '2px solid #fff',
    outlineOffset: '2px',
  },
  buttonSecondary: {
    backgroundColor: '#6b7280',
  },
  tableContainer: {
    backgroundColor: '#fff',
    borderRadius: '0.5rem',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '1rem',
    textAlign: 'left',
    backgroundColor: '#f3f4f6',
    fontWeight: '600',
    color: '#374151',
    borderBottom: '2px solid #e5e7eb',
    fontSize: '0.875rem',
  },
  tr: {
    borderBottom: '1px solid #e5e7eb',
  },
  td: {
    padding: '1rem',
    color: '#374151',
    fontSize: '0.875rem',
  },
  code: {
    backgroundColor: '#f3f4f6',
    padding: '0.25rem 0.5rem',
    borderRadius: '0.25rem',
    fontSize: '0.875rem',
    fontFamily: 'monospace',
  },
  targetType: {
    color: '#6b7280',
    fontSize: '0.875rem',
  },
  badge: {
    padding: '0.25rem 0.75rem',
    borderRadius: '9999px',
    fontSize: '0.875rem',
    fontWeight: '500',
    display: 'inline-block',
  },
  linkButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '0.25rem',
    textDecoration: 'underline',
    color: '#3b82f6',
    outline: 'none',
    borderRadius: '0.25rem',
  },
  linkButtonFocus: {
    outline: '2px solid #3b82f6',
    outlineOffset: '2px',
  },
  reason: {
    color: '#374151',
    fontSize: '0.875rem',
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'inline-block',
  },
  muted: {
    color: '#9ca3af',
    fontSize: '0.875rem',
  },
  loadMore: {
    marginTop: '1rem',
    textAlign: 'center',
  },
};
